import Preferences from '@/creditflowresearch/domain/Preferences';
import Market, { EmergingMarket } from '@/creditflowresearch/domain/Market';
import Product from '@/creditflowresearch/domain/Product';
import Region from '@/creditflowresearch/domain/Region';
import Segment from '@/creditflowresearch/domain/Segment';
import MarketOverviewUrlResolver from '@/creditflowresearch/infrastructure/routing/MarketOverviewUrlResolver';
import { getQueryString } from '@framework/router';
import { getSegmentScopes } from '@/creditflowresearch/domain/Segment/selectors/selectSegmentVisibility';
import { selectScopedSegmentIds } from '@/creditflowresearch/application/Preferences/selectAccessibleMappedIds';

class MarketOverviewUrlBuilder {
  private readonly urlResolver: MarketOverviewUrlResolver;

  constructor(urlResolver: MarketOverviewUrlResolver) {
    this.urlResolver = urlResolver;
  }

  build(preferences: Preferences): string | null {
    const entity = this.getMarketOverviewEntity(preferences);

    if (!entity) {
      return null;
    }

    const baseUrl = this.urlResolver.resolve(entity) || '';
    const segmentIds = this.getSegmentIds(preferences, entity);
    const query = this.buildQuery(preferences, segmentIds);

    return `${ baseUrl }?${ getQueryString(query) }`;
  }

  /** The landing page shows one row, so it carries that row's segments. ABS owns none. */
  private getSegmentIds(preferences: Preferences, entity: Market | Product): string[] {
    const marketIds = preferences.getMarketIds();
    const [scope] = getSegmentScopes({ productIds: preferences.getProductIds(), marketIds });
    const saved = scope ? selectScopedSegmentIds(preferences, scope, marketIds) : [];

    return saved.length ? saved : (entity.getSegmentIds() || []);
  }

  private getMarketOverviewEntity(preferences: Preferences): Market | Product | null {
    const marketIds = preferences.getMarketIds();
    const productIds = preferences.getProductIds();
    const hasEmMarket = marketIds.includes(EmergingMarket.MARKET_ID);

    return hasEmMarket ? Market.from(EmergingMarket.MARKET_ID) : Product.from(productIds[0]);
  }

  private buildQuery(preferences: Preferences, segmentIds: string[] = []): Record<string, string[]> {
    const productIds = preferences.getProductIds();
    const regionIds = preferences.getRegionIds();
    const marketIds = preferences.getMarketIds();

    return {
      products: Product.getAbbreviations(productIds),
      regions: regionIds.length ? Region.getAbbreviations(regionIds) : [],
      markets: Market.getAbbreviations(marketIds),
      segments: Segment.getAbbreviations(segmentIds),
    };
  }
}

export default MarketOverviewUrlBuilder;
